import { Box, Chip, Tooltip, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ExoAvatar from '../../../components/exo/ExoAvatar';
import { tokens } from '../../../global/theme/tokens';
import { secondsToHours, projectSharePercent, formatPercent } from './workTimeReportUtils';
import WorkTimeReportWorkPackagesDialog from './WorkTimeReportWorkPackagesDialog';

const WorkTimeReportProjectItem = ({ projectData, userId, user, startDate, endDate }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);

  const { project, workedTimeInSeconds, totalWorkedTimeInSeconds } = projectData;
  const share = projectSharePercent(projectData);
  const shareWidth = share === null ? 0 : Math.min(share, 100);
  const accent = project.isInternal ? colors.info : colors.success;

  const navigateToProject = (e) => {
    e.stopPropagation();
    navigate(`/projects/management/${project.id}`);
  };

  return (
    <>
      <Box
        onClick={() => setDialogOpen(true)}
        className="flex items-center gap-3 p-3 rounded-lg backdrop-blur-lg cursor-pointer transition-opacity"
        sx={{
          bgcolor: colors.card,
          '&:hover': { opacity: 0.9 },
        }}
      >
        <Tooltip title={t('Open project')} arrow>
          <Box
            onClick={navigateToProject}
            className="rounded transition-transform"
            sx={{
              cursor: 'pointer',
              '&:hover': { transform: 'scale(1.05)' },
            }}
          >
            {project.image ? (
              <img
                src={project.image.temporaryUrl}
                alt=""
                className="w-9 h-9 object-contain rounded flex-shrink-0"
              />
            ) : (
              <ExoAvatar name={project.name} size="36px" type="rounded" />
            )}
          </Box>
        </Tooltip>

        {project.nr && (
          <Typography
            variant="caption"
            sx={{ opacity: 0.6, fontFamily: 'monospace', flexShrink: 0 }}
          >
            #{project.nr}
          </Typography>
        )}

        <Tooltip title={t('Open project')} arrow placement="top-start">
          <Typography
            onClick={navigateToProject}
            sx={{
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              borderBottom: '1px dashed transparent',
              transition: 'border-color 0.2s ease',
              '&:hover': { borderBottomColor: accent },
            }}
          >
            {project.name}
          </Typography>
        </Tooltip>

        <Chip
          size="small"
          label={project.isInternal ? t('Internal') : t('Customer')}
          sx={{
            bgcolor: `${accent}30`,
            color: accent,
            fontWeight: 600,
            border: `1px solid ${accent}40`,
          }}
        />

        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 60, textAlign: 'right' }}>
          {secondsToHours(workedTimeInSeconds)} h
        </Typography>

        <Box className="flex items-center gap-2 min-w-[120px]">
          <Box
            sx={{
              width: 60,
              height: 4,
              bgcolor: colors.glass,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${shareWidth}%`,
                height: '100%',
                bgcolor: accent,
                transition: 'width 0.3s ease',
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ opacity: 0.7, minWidth: 40 }}>
            {formatPercent(share)}
          </Typography>
        </Box>
      </Box>

      <WorkTimeReportWorkPackagesDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        userId={userId}
        projectId={project.id}
        user={user}
        project={project}
        startDate={startDate}
        endDate={endDate}
        totalProjectSeconds={totalWorkedTimeInSeconds}
      />
    </>
  );
};

export default WorkTimeReportProjectItem;
